import type { PaymentAdapter } from '@payloadcms/plugin-ecommerce/types'
import type { CollectionSlug } from 'payload'

/**
 * Pickup in Store (Click & Collect) Payment Adapter
 *
 * This adapter handles in-store pickup orders where the customer:
 * 1. Places the order online
 * 2. Receives confirmation with pickup instructions
 * 3. Picks up the order at the store (usually ready in 2–3 days if in stock)
 *
 * Key difference from other adapters:
 * - Shipping cost is ALWAYS €0 (customer picks up in store)
 * - No bank details or mailing address needed
 * - Special email with pickup instructions and store address
 */
export const pickupAdapter: PaymentAdapter = {
  name: 'pickup',
  label: 'Pickup in Store',

  /**
   * For pickup, "initiating payment" just means preparing the transaction record.
   * Payment happens at the store when the customer picks up.
   */
  initiatePayment: async ({
    data: { cart, currency, customerEmail, billingAddress, shippingAddress },
    req,
    transactionsSlug,
  }) => {
    // Create a transaction record for tracking
    const transaction = await req.payload.create({
      collection: transactionsSlug as CollectionSlug,
      data: {
        currency,
        amount: cart.subtotal || 0,
        status: 'pending',
        paymentMethod: 'pickup',
      } as any,
    })

    return {
      message: 'Pickup order initiated. You will be notified when your order is ready.',
      transactionID: transaction.id,
    }
  },

  /**
   * Confirm the order for in-store pickup.
   * Creates the order record with 'pending' status.
   * Shipping cost is forced to 0.
   */
  confirmOrder: async ({
    data,
    req,
    ordersSlug = 'orders',
    transactionsSlug = 'transactions',
    cartsSlug = 'carts',
    customersSlug = 'users',
  }) => {
    const {
      customerEmail,
      billingAddress,
      shippingAddress,
      emailConsent,
      cartID,
      // Extract passed totals from frontend
      orderSubtotal,
      orderTotal,
      shippingCost: _shippingCostIgnored, // Ignore frontend shipping — pickup is always free
      bulkVolumeDiscount,
      manualCouponDiscount,
      couponId,
      orderLocale,
      ...additionalData
    } = data

    // Get the user from the request (if authenticated)
    const user = req.user

    // Get the cart - first try by cartID from data, then by customer
    let cart: any

    if (cartID) {
      cart = await req.payload.findByID({
        collection: cartsSlug as CollectionSlug,
        id: cartID as string,
        depth: 2,
        overrideAccess: true,
      })
    } else if (user) {
      const cartsResult = await req.payload.find({
        collection: cartsSlug as CollectionSlug,
        where: {
          customer: { equals: user.id },
        },
        limit: 1,
        depth: 2,
        overrideAccess: true,
      })
      cart = cartsResult.docs[0]
    }

    if (!cart || !cart.items || cart.items.length === 0) {
      throw new Error('No cart found or cart is empty')
    }

    // Generate a unique receipt ID (format: STM-YYYYMMDD-XXXXX)
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
    const randomPart = Math.floor(Math.random() * 100000)
      .toString()
      .padStart(5, '0')
    const receiptId = `STM-${dateStr}-${randomPart}`

    // Force shipping to 0 for pickup orders
    const finalShippingCost = 0
    const finalSubtotal = orderSubtotal ?? cart.subtotal ?? 0
    const finalBulkDiscount = bulkVolumeDiscount ?? 0
    const finalCouponDiscount = manualCouponDiscount ?? 0
    const finalCouponId = couponId ?? null

    // Recalculate total without shipping
    const finalTotal =
      orderTotal != null
        ? // If frontend sent a total, we trust the subtotal minus discounts but force shipping=0
          Math.max(0, finalSubtotal - finalBulkDiscount - finalCouponDiscount + finalShippingCost)
        : (cart.subtotal ?? 0)

    // Create a transaction record
    const transaction = await req.payload.create({
      collection: transactionsSlug as CollectionSlug,
      data: {
        currency: cart.currency || 'EUR',
        amount: finalTotal,
        status: 'pending',
        paymentMethod: 'pickup',
      } as any,
      overrideAccess: true,
    })

    // Build order items from cart items
    const orderItems = cart.items.map((item: any) => {
      const product = typeof item.product === 'string' ? item.product : item.product?.id
      const variant = item.variant
        ? typeof item.variant === 'string'
          ? item.variant
          : item.variant?.id
        : undefined

      // Calculate item price
      let price = 0

      if (item.selectedVariantData?.priceTotal != null) {
        price = Math.round(Number(item.selectedVariantData.priceTotal) * 100)
      }

      if (price === 0 && typeof item.product === 'object' && item.product) {
        // First try: variant document from the variants collection (ecommerce plugin pattern)
        if (typeof item.variant === 'object' && item.variant) {
          const variantPrice = (item.variant as any).priceInEUR || (item.variant as any).price
          if (variantPrice != null) {
            price = Math.round(Number(variantPrice) * 100)
          }
        }
        // Second try: embedded variants on the product (legacy pattern)
        if (price === 0 && item.selectedVariants && item.product.variants) {
          for (const variantDef of item.product.variants) {
            const selectedValue = item.selectedVariants?.[variantDef.name]
            if (selectedValue) {
              const valueObj = variantDef.values?.find((v: any) => v.value === selectedValue)
              if (valueObj?.price !== null && valueObj?.price !== undefined) {
                price = Math.round(Number(valueObj.price) * 100)
                break
              }
            }
          }
        }
        // Fall back to base price
        if (price === 0 && item.product.basePrice) {
          price = Math.round(Number(item.product.basePrice) * 100)
        }
      }

      return {
        product,
        quantity: item.quantity,
        price,
        // Bake the product's custom tax rate into the order item
        tax_rate: typeof item.product === 'object' && item.product
          ? (item.product as any).customTaxRate ?? undefined
          : undefined,
        ...(variant ? { variant } : {}),
        ...(item.selectedVariants ? { selectedVariants: item.selectedVariants } : {}),
        ...(item.selectedVariantData ? { selectedVariantData: item.selectedVariantData } : {}),
      }
    })

    // Get customer email from billing address or user or cart
    const cartCustomerEmail = typeof cart?.customer === 'object' && cart?.customer
      ? (cart.customer as any).email
      : null

    const orderCustomerEmail =
      billingAddress?.email ||
      customerEmail ||
      user?.email ||
      cartCustomerEmail ||
      null // explicit null — don't save undefined

    const order = await req.payload.create({
      collection: ordersSlug as CollectionSlug,
      data: {
        receiptId,
        customer: user?.id ?? null,
        customerEmail: orderCustomerEmail,
        items: orderItems,
        amount: finalTotal,
        total: finalTotal,
        currency: cart.currency || 'EUR',
        status: 'pending',
        paymentMethod: 'pickup',
        transaction: transaction.id,
        orderLocale: (orderLocale as string) || 'en',
        shippingCost: finalShippingCost,
        // Store shipping info
        shipping: {
          shippingCost: finalShippingCost,
        },
        // Store discount info
        discounts: {
          discountAmount: finalBulkDiscount + finalCouponDiscount,
          bulkVolumeDiscount: finalBulkDiscount,
          manualCouponDiscount: finalCouponDiscount,
          appliedCouponId: finalCouponId,
        },
        ...(billingAddress ? { billingAddress } : {}),
        // For pickup, shipping address = store address, but we store billing as fallback
        ...(shippingAddress
          ? { shippingAddress: shippingAddress || billingAddress }
          : billingAddress
            ? { shippingAddress: billingAddress }
            : {}),
        ...additionalData,
      } as any,
      overrideAccess: true,
    })

    // Clear the cart after successful order creation
    if (cart.id) {
      await req.payload.update({
        collection: cartsSlug as CollectionSlug,
        id: cart.id,
        data: {
          items: [],
        } as any,
        overrideAccess: true,
      })
    }

    return {
      message:
        'Order created successfully. You will be notified when your order is ready for pickup.',
      orderID: order.id,
      transactionID: transaction.id,
      receiptId,
    }
  },

  /**
   * Group field for admin panel.
   */
  group: {
    name: 'pickup',
    type: 'group',
    admin: {
      condition: (data) => data?.paymentMethod === 'pickup',
    },
    fields: [],
  },

  endpoints: [],
}
